/**
 * The label of a vector layer's switch in Map settings, in both apps.
 *
 * A layer's `label` is its config layer's `display_name`, English text set
 * in the backend presets: "MSLP" for the isobars, "10m wind" for the wind
 * barbs, "Fronts" (every preset vector layer in prod, 2026-10-16). Shown
 * raw, a French user saw and heard English, and "10m wind" sat next to the
 * Wind animation switch. So a preset's label is replaced by the translated
 * name of what the layer draws. Any other label is a builder config's own
 * text (`display_name` is free) and is shown as written.
 *
 * Only the visible label: a switch is remembered by `forecastVectorLayerKey`
 * (`type:label`, the raw label), so stored choices are untouched.
 */
import {messages} from '@smartmixin/i18n';
import type {MessageDescriptor} from 'react-intl';

import type {ForecastMapVectorLayerSpec} from '../../api/types';

type FormatMessage = (descriptor: MessageDescriptor) => string;

/** Per layer type: the backend preset's `display_name`, and its name. */
const PRESET_LABELS: Record<
  ForecastMapVectorLayerSpec['type'],
  {label: string; message: MessageDescriptor}
> = {
  contour: {label: 'MSLP', message: messages.forecastMaps.vectorLayerIsobars},
  barbs: {
    label: '10m wind',
    message: messages.forecastMaps.vectorLayerWindBarbs,
  },
  fronts: {label: 'Fronts', message: messages.forecastMaps.vectorLayerFronts},
};

export const forecastVectorLayerLabel = (
  spec: Pick<ForecastMapVectorLayerSpec, 'type' | 'label'>,
  formatMessage: FormatMessage,
): string => {
  // A type from a newer server has no entry: `undefined`, kept by `?.`. No
  // own-key guard: a type naming an inherited property (`constructor`)
  // finds a value with no `label`, which never equals the layer's label
  // (a non-empty string), so the label comparison is the whole check.
  const preset = PRESET_LABELS[spec.type];
  return preset?.label === spec.label
    ? formatMessage(preset.message)
    : spec.label;
};
